import { Trans } from "@lingui/macro";
import { useState } from "react";
import { Camera, X, Upload, Loader2 } from "lucide-react";
import { UUID } from "../../models/local/Uid";
import { Fire } from "../../services/FireClient";
import { ResizeImage } from "../../services/Utils/ImageResizer";

import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { cn } from "../../lib/utils";
import { UserAvatar } from "./UserAvatar";

interface ImageProps {
  onSelected: (imgUrl: string) => void,
  loginImage: string,
  name?: string
}

const PRESET_ICONS = [
  "/assets/profiles/avatar_cat.png",
  "/assets/profiles/avatar_dog.png",
  "/assets/profiles/avatar_rabbit.png",
  "/assets/profiles/avatar_bear.png",
  "/assets/profiles/avatar_chick.png",
  "/assets/profiles/avatar_fox.png",
  "/assets/profiles/avatar_unicorn.png",
  "/assets/profiles/avatar_dragon.png",
  "/assets/profiles/avatar_alien.png",
  "/assets/profiles/avatar_robot.png",
  "/assets/profiles/avatar_strawberry.png",
  "/assets/profiles/avatar_melon.png",
  "/assets/profiles/avatar_grape.png",
  "/assets/profiles/avatar_game.png",
  "/assets/profiles/avatar_sunflower.png",
  "/assets/profiles/avatar_cactus.png",
  "/assets/profiles/avatar_planet.png",
  "/assets/profiles/avatar_rocket.png",
  "/assets/profiles/avatar_panda.png",
  "/assets/profiles/avatar_koala.png",
  "/assets/profiles/avatar_tiger.png",
  "/assets/profiles/avatar_penguin.png"
];

const SelectProfileImageModal: React.FC<ImageProps> = ({ loginImage, onSelected, name }) => {
  const [inProgress, setInProgress] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [profileImage, setProfileImage] = useState(loginImage);
  const [chosenImageFile, setChosenImageFile] = useState<File>();
  const { toast } = useToast();

  async function profileImgChanged(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) {
      const _file = e.target.files[0];
      const resized = await ResizeImage(_file, 250);
      setProfileImage(URL.createObjectURL(resized));
      setChosenImageFile(resized);
    }
  }

  function saveSelectedImage() {
    if (chosenImageFile && profileImage.startsWith('blob')) { // Obviously an image.
      setInProgress(true);
      Fire.uploadFile(chosenImageFile, `/profiles/${UUID.get()}.jpg`, url => {
        if (url) {
          onSelected(url);
          setProfileImage(url);
          setInProgress(false);
          setShowModal(false);
        } else {
          toast({ title: "Failed to upload image" });
          setInProgress(false);
        }
      }, null);
    } else {
      onSelected(profileImage);
      setShowModal(false);
    }
  }

  function handleIconSelect(iconUrl: string) {
    setChosenImageFile(undefined);
    setProfileImage(iconUrl);
  }

  return (
    <>
      <Button
        variant="secondary"
        size="icon"
        className="absolute bottom-0 right-0 h-8 w-8 rounded-[var(--radius-avatar)] shadow-md z-10"
        onClick={() => setShowModal(true)}
        data-cy="upload"
      >
        <Camera className="w-4 h-4 text-gray-500" />
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-sm w-[95vw] overflow-visible">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold"><Trans>Profile Image</Trans></h2>
          </div>

          <div className="flex flex-col items-center gap-6">
            {/* Preview */}
            <div className="relative">
              <UserAvatar
                src={profileImage}
                name={name}
                className="w-24 h-24 border border-gray-100"
              />
            </div>

            {/* Icon Grid */}
            <div className="w-full">
              <div className="grid grid-cols-4 gap-3 p-2">
                {/* Upload Button */}
                <button
                  className="w-12 h-12 rounded-[var(--radius-avatar)] border border-dashed border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  onClick={() => document.getElementById('image-input')?.click()}
                  title="Upload image"
                >
                  <Upload className="w-5 h-5 text-gray-400" />
                </button>

                {/* Preset Icons */}
                {PRESET_ICONS.map((iconUrl, index) => (
                  <UserAvatar
                    key={index}
                    src={iconUrl}
                    className={cn(
                      "w-12 h-12 cursor-pointer border border-transparent hover:border-orange-200 hover:bg-orange-50 transition-all p-1",
                      profileImage === iconUrl && "border-orange-500 bg-orange-50 ring-2 ring-orange-100"
                    )}
                    onClick={() => handleIconSelect(iconUrl)}
                  />
                ))}
              </div>
            </div>

            <input
              id="image-input"
              type='file'
              accept="image/*"
              onChange={profileImgChanged}
              hidden
            />

            {/* Actions */}
            <div className="flex w-full gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setProfileImage(""); setChosenImageFile(undefined); }}
              >
                <Trans>Reset</Trans>
              </Button>
              <Button
                className="flex-1"
                onClick={saveSelectedImage}
                disabled={inProgress}
                data-cy="save"
              >
                {inProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                <Trans>Save</Trans>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SelectProfileImageModal;
